/**
 * `/app/return`: the payment page's return address (see ../paymentReturn).
 * The website's customer goes straight back to their visit; the app's is
 * handed back to the ASEED app, with a way home if no app answers.
 */
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AppBar, Spinner } from "../components";
import { useI18n } from "../i18n";
import { ApiError, redeemReturn } from "../api";
import { handoffVisit, returnTarget } from "../paymentReturn";
import { store } from "../storage";

export function ReturnScreen() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { search } = useLocation();
  const [target] = useState(() => returnTarget(store.get("paying"), search));
  const [stillHere, setStillHere] = useState(false);
  // Another browser: ask before showing the visit here (view only).
  const [ask, setAsk] = useState(false);
  const [busy, setBusy] = useState(false);

  const resume = (visitId: string) => {
    store.remove("paying");
    store.set("visit", visitId);
    navigate(`/visit/${encodeURIComponent(visitId)}`, { replace: true });
  };

  useEffect(() => {
    if (target.kind === "token") {
      // A one-time key: out of the address bar and the history at once.
      navigate("/return", { replace: true });
      let live = true;
      redeemReturn(target.token)
        .then((r) => live && resume(r.vend_session_id))
        .catch((e) => {
          if (live && e instanceof ApiError && e.code === "return_other_browser") {
            setAsk(true);
            return;
          }
          // Used up (a reload), expired, or another browser: this browser's own
          // record, else "check where you started". Never the app: `rt` means
          // the website started this payment.
          if (!live) return;
          const known = handoffVisit(store.get("paying")) ?? store.get("visit");
          if (known) resume(known);
          else setStillHere(true);
        });
      return () => {
        live = false;
      };
    }
    if (target.kind === "website") {
      store.remove("paying");
      store.set("visit", target.visitId);
      navigate(target.path, { replace: true });
      return;
    }
    window.location.href = target.href;
    // If no app took the link, this page is still showing.
    const timer = setTimeout(() => setStillHere(true), 1500);
    return () => clearTimeout(timer);
  }, [navigate, target]);

  function showHere() {
    if (target.kind !== "token") return;
    setBusy(true);
    redeemReturn(target.token, true)
      .then((r) => resume(r.vend_session_id))
      .catch(() => {
        setAsk(false);
        setStillHere(true);
      })
      .finally(() => setBusy(false));
  }

  return (
    <div className="aseed-shell">
      <AppBar />
      <main className="aseed-shell__main">
        <div className="aseed-shell__content v-stack">
          {ask ? (
            <section className="v-card v-stack">
              <p>{t("vend.return.otherBrowser")}</p>
              <button type="button" className="aseed-button v-wide" disabled={busy} onClick={showHere}>
                {busy ? <Spinner /> : null}
                {t("vend.return.showHere")}
              </button>
              <button
                type="button"
                className="aseed-button v-button-ghost v-wide"
                onClick={() => navigate("/", { replace: true })}
              >
                {t("vend.home")}
              </button>
            </section>
          ) : stillHere && target.kind !== "website" ? (
            <section className="v-card v-stack">
              <p>{t("vend.return.elsewhere")}</p>
              {target.kind === "app" ? (
                <a className="aseed-button v-wide" href={target.href}>
                  {t("vend.return.openApp")}
                </a>
              ) : null}
              <button
                type="button"
                className="aseed-button v-button-ghost v-wide"
                onClick={() => navigate("/", { replace: true })}
              >
                {t("vend.home")}
              </button>
            </section>
          ) : (
            <div className="v-center">
              <Spinner label={t("vend.return.checking")} />
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
